import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import DashboardCards from '../../../components/shared/dashboard-card/DashboardCard';
import { IAccountManagement } from '../../../interfaces/account/IAccountChatbox';
import Select from '../../../shared/select';
import moment from 'moment';
import { useOverallAccountManagementMutation } from '../../../store/chatbox/chatboxApi';
import { PieChart, Pie, Cell } from 'recharts';
import DetailedView from './DetailedView';
import DashboardStats from './DashboardStats';
import { useGetHotelClientsQuery } from '../../../store/user/userApi';
import { useTheme } from '../../../utils/theme-context';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
const dateOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'all_time', label: 'All Time' },
];

const getDateRange = (option: string) => {
    const now = moment();

    switch (option) {
        case 'today':
            return {
                dateFrom: moment().startOf('day').toDate(),
                dateTo: moment().endOf('day').toDate(),
            };

        case 'yesterday':
            return {
                dateFrom: moment().subtract(1, 'day').startOf('day').toDate(),
                dateTo: moment().subtract(1, 'day').endOf('day').toDate(),
            };

        case 'last_week': {
            const start = moment().subtract(1, 'week').startOf('isoWeek');
            const end = moment().subtract(1, 'week').endOf('isoWeek');
            return {
                dateFrom: start.toDate(),
                dateTo: end.toDate(),
            };
        }

        case 'last_month':
            return {
                dateFrom: moment()
                    .subtract(1, 'month')
                    .startOf('month')
                    .toDate(),
                dateTo: moment().subtract(1, 'month').endOf('month').toDate(),
            };

        case 'last_6_months':
            return {
                dateFrom: moment()
                    .subtract(6, 'months')
                    .startOf('day')
                    .toDate(),
                dateTo: now.endOf('day').toDate(),
            };

        case 'all_time':
            return {
                dateFrom: moment('1990-01-01').toDate(),
                dateTo: now.endOf('day').toDate(),
            };

        default:
            return {
                dateFrom: undefined,
                dateTo: undefined,
            };
    }
};

const DashboardFilters = () => {
    const { theme } = useTheme()
    const [isActiveTab, setIsActiveTab] = useState<string>('main-view');

    const { data: hotelClients } = useGetHotelClientsQuery();

    const [overallAccountManagement, { data }] =
        useOverallAccountManagementMutation();
    const getAccountInfo = async (hotelId: string) => {
        try {
            await overallAccountManagement({
                hotelId
            }).unwrap();
        } catch (e) {
            console.error({ e });
        }
    };
    const { register, handleSubmit, watch, setValue } = useForm<IAccountManagement>();


    useEffect(() => {
        if (watch('hotelId')) {
            getAccountInfo(watch('hotelId'))
        }

    }, [watch('hotelId')])

    useEffect(() => {
        if (hotelClients?.data)
            setValue('hotelId', hotelClients.data[0]._id)
    }, [hotelClients])

    const handleTabSelect = (tab: string) => setIsActiveTab(tab);

    const onSubmit = async ({ timeRange, hotelId }: IAccountManagement) => {
        const { dateFrom, dateTo } = getDateRange(timeRange || '');
        try {
            await overallAccountManagement({ hotelId, dateFrom, dateTo });
        } catch (e) {
            console.error({ e });
        }
    };

    useEffect(() => {
        if (watch('timeRange')) {
            handleSubmit(onSubmit)();
        }
    }, [watch('timeRange')]);

    const chartData = [
        { name: 'Positive', value: data?.data[0]?.favPct || 0 },
        { name: 'No Rating', value: data?.data[0]?.noRatingPct || 0 },
        { name: 'Negative', value: data?.data[0]?.issuesPct || 0 },
    ];

    const hotelData = hotelClients?.data.map((client => ({ value: client._id, label: client.name }))) || []

    return (
        <>
            <div className='border border-gray-200 rounded-2xl p-5'>
                <h4 className='flex font-medium text-xl mb-0.5'>
                    <img
                        alt='filters'
                        className='mr-1 w-[25px] object-contain'
                        src={theme === 'dark' ? '/assets/shared/filter-icon-white.svg' : '/assets/shared/filters.svg'}
                    />
                    Dashboard Filters
                </h4>
                <p className='text-gray-500 text-sm'>
                    Filter data by specific clients and agents to focus your
                    analysis
                </p>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className='w-full flex flex-wrap -mx-2 mt-3'>
                    <Select
                        name='Client'
                        containerStyle='w-full md:w-6/12 px-2'
                        register={register('hotelId')}
                        options={hotelData}
                    />
                    <Select
                        name='Time Range'
                        containerStyle='w-full md:w-6/12 px-2'
                        register={register('timeRange')}
                        options={dateOptions}
                    />
                </form>
                <div className='w-full bg-gray-100 dark:bg-black p-1.5 rounded-3xl'>
                    <button
                        type='button'
                        className={`w-full md:w-6/12 text-center p-1.5 rounded-3xl ${isActiveTab === 'main-view' ? 'bg-gray-800' : ''
                            }`.trim()}
                        onClick={() => handleTabSelect('main-view')}>
                        <span
                            className={
                                isActiveTab === 'main-view'
                                    ? 'text-white'
                                    : 'text-gray-700'
                            }>
                            Main View
                        </span>
                    </button>
                    <button
                        type='button'
                        className={`w-full md:w-6/12 text-center p-1.5 rounded-3xl ${isActiveTab === 'detailed-view' ? 'bg-gray-800' : ''
                            }`.trim()}
                        onClick={() => handleTabSelect('detailed-view')}>
                        <span
                            className={
                                isActiveTab === 'detailed-view'
                                    ? 'text-white'
                                    : 'text-gray-700'
                            }>
                            Detailed View
                        </span>
                    </button>
                </div>
            </div>
            {isActiveTab === 'main-view' ? (
                <>
                    <div className='flex flex-wrap my-4 -mx-2'>
                        <DashboardCards
                            title='Total Number of queries'
                            numbers={data?.data[0]?.total || 0}
                        />
                        <DashboardCards
                            title='Positive Rating'
                            numbers={data?.data[0]?.totalLiked || 0}
                            color='#10B981'
                        />
                        <DashboardCards
                            title='Negative Rating'
                            numbers={data?.data[0]?.totalDisliked || 0}
                            color='#EF4444'
                        />
                        <DashboardCards
                            title='Favorite Rating'
                            numbers={data?.data[0]?.totalFav || 0}
                        />
                    </div>
                    <div className='flex flex-wrap my-2 -mx-2'>
                        <div className='w-full md:w-8/12 px-2'>
                            <div className='border border-gray-200 rounded-2xl p-6'>
                                <h5 className='text-sm font-normal text-[#1C1C1C] mb-3 dark:text-white'>
                                    Queries run by document in the repository
                                </h5>
                                <div className='bg-gray-200 dark:bg-black rounded-2xl w-full h-60' />
                            </div>
                        </div>
                        <div className='w-full md:w-4/12 px-2'>
                            <div className='border border-gray-200 rounded-2xl p-6 h-full'>
                                <h5 className='text-sm font-normal text-[#1C1C1C] dark:text-white'>
                                    Rating distribution
                                </h5>
                                <div className='flex flex-wrap items-center h-full'>
                                    <PieChart width={120} height={120}>
                                        <Pie
                                            data={chartData}
                                            cx='50%'
                                            cy='50%'
                                            innerRadius={40}
                                            outerRadius={60}
                                            fill='#8884d8'
                                            paddingAngle={5}
                                            dataKey='value'>
                                            {chartData.map((_entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={
                                                        COLORS[
                                                        index %
                                                        COLORS.length
                                                        ]
                                                    }
                                                />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                    <div className='pl-8'>
                                        {chartData.map((entry, index) => (
                                            <div
                                                key={entry.name}
                                                className='flex flex-row items-center text-xs mb-3'>
                                                <div
                                                    className='w-2 h-2 rounded-full mr-2'
                                                    style={{
                                                        backgroundColor:
                                                            COLORS[
                                                            index %
                                                            COLORS.length
                                                            ],
                                                    }}
                                                />
                                                <h4 className='mr-4'>
                                                    {entry.name}
                                                </h4>
                                                <p className='ml-auto'>
                                                    {Number(
                                                        entry.value,
                                                    ).toFixed(2)}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <DashboardStats hotelId={watch('hotelId')} />
                </>
            ) : <DetailedView hotelId={watch('hotelId')} />}
        </>
    );
};

export default DashboardFilters;
